import { useEffect, useState } from "react";
import { useTenant } from "@/lib/tenant";
import { Loader2, Save, ExternalLink, CheckCircle2, AlertCircle, Unlink, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function XeroConnect() {
  const { api } = useTenant();
  const [status, setStatus] = useState(null);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/xero/status").then(r => {
    setStatus(r.data);
    if (r.data.client_id_set) setClientId("•••••• (set — re-enter to change)");
  });
  useEffect(() => { load(); }, []); // eslint-disable-line

  // ?connected=1 returned after OAuth — show success toast and refresh status
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("connected")) {
      toast.success("Xero connected!");
      load();
    }
  }, []); // eslint-disable-line

  const saveConfig = async () => {
    if (!clientId.startsWith("••") && !clientSecret) {
      toast.error("Enter both Client ID and Client Secret");
      return;
    }
    setBusy(true);
    try {
      const payload = {};
      if (!clientId.startsWith("••")) payload.client_id = clientId.trim();
      if (clientSecret) payload.client_secret = clientSecret.trim();
      // If only one provided, still send both — backend requires both
      if (!payload.client_id || !payload.client_secret) {
        toast.error("Enter both Client ID and Client Secret");
        setBusy(false);
        return;
      }
      await api.put("/admin/xero/config", payload);
      toast.success("Saved");
      setClientSecret("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.get("/auth/xero/login");
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start OAuth");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Xero? Operators will see manual client list only until you reconnect.")) return;
    try { await api.delete("/admin/xero/disconnect"); load(); toast.success("Disconnected"); }
    catch (e) { toast.error("Failed"); }
  };

  if (!status) return <div className="p-8 text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>;

  return (
    <div className="p-8 max-w-3xl">
      <h1 className="text-2xl font-bold mb-1">Xero Integration</h1>
      <p className="text-sm text-slate-400 mb-6">
        Connect Xero so operators can pick the right customer when logging diary entries. Read-only — we only pull your customer list, nothing is written to Xero.
      </p>

      {/* Connection status */}
      <div className={`border rounded-lg p-4 mb-6 ${status.connected ? "bg-emerald-500/5 border-emerald-500/30" : "bg-amber-500/5 border-amber-500/30"}`}>
        {status.connected ? (
          <div className="flex items-start gap-3">
            <CheckCircle2 className="text-emerald-400 mt-0.5" size={20} />
            <div className="flex-1">
              <div className="font-bold text-emerald-300">Connected to Xero</div>
              <div className="text-xs text-slate-400 mt-1">
                Org: <span className="text-white">{status.tenant_name || status.tenant_id || "—"}</span>
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">Scope: {status.scope}</div>
            </div>
            <button onClick={disconnect} className="text-slate-400 hover:text-red-300 text-sm flex items-center gap-1">
              <Unlink size={14} /> Disconnect
            </button>
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <AlertCircle className="text-amber-400 mt-0.5" size={20} />
            <div>
              <div className="font-bold text-amber-300">Not connected</div>
              <div className="text-xs text-slate-400 mt-1">Save your Xero app credentials below, then click Connect.</div>
            </div>
          </div>
        )}
      </div>

      {/* Setup guide */}
      <details className="bg-[#0a0e1c] border border-white/10 rounded-lg p-4 mb-6">
        <summary className="cursor-pointer text-sm font-bold text-amber-300">How to get your Xero client_id and client_secret</summary>
        <ol className="text-sm text-slate-300 space-y-2 mt-3 list-decimal list-inside">
          <li>Go to <a href="https://developer.xero.com/app/manage" target="_blank" rel="noopener" className="text-amber-400 hover:underline inline-flex items-center gap-1">developer.xero.com/app/manage <ExternalLink size={11} /></a> and sign in with your Xero account.</li>
          <li>Click <strong>New app</strong> → choose <strong>Web app</strong>.</li>
          <li>App name: <code className="text-amber-300">Wilson Field App</code> (anything is fine)</li>
          <li>Company URL: <code className="text-amber-300">https://wilson-contractors.co.uk</code></li>
          <li>Redirect URI: <code className="text-amber-300 break-all">{status.redirect_uri || `${window.location.origin}/api/auth/xero/callback`}</code> — copy this exactly.</li>
          <li>Save the app. On the next screen, copy the <strong>Client ID</strong> and generate a <strong>Client Secret</strong>.</li>
          <li>Paste both below → Save → Connect.</li>
        </ol>
      </details>

      {/* Credentials form */}
      <div className="bg-[#0a0e1c] border border-white/10 rounded-lg p-5 mb-6">
        <h2 className="font-bold mb-3 flex items-center gap-2"><KeyRound size={16} className="text-amber-400" /> Xero App credentials</h2>
        <div className="space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-widest text-slate-400 mb-1.5">Client ID</label>
            <input value={clientId} onChange={e => setClientId(e.target.value)} data-testid="xero-client-id"
                   className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white font-mono text-sm" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-slate-400 mb-1.5">
              Client Secret {status.client_secret_set && <span className="text-slate-500 normal-case">(set — re-enter to change)</span>}
            </label>
            <input type="password" value={clientSecret} onChange={e => setClientSecret(e.target.value)} data-testid="xero-client-secret"
                   placeholder={status.client_secret_set ? "••••••" : ""}
                   className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white font-mono text-sm" />
          </div>
          <button onClick={saveConfig} disabled={busy} data-testid="xero-save"
                  className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-[#0a0e1c] font-bold px-4 py-2 rounded-lg flex items-center gap-1.5">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save credentials
          </button>
        </div>
      </div>

      {/* Connect button */}
      {status.client_id_set && status.client_secret_set && (
        <button onClick={connect} disabled={busy} data-testid="xero-connect"
                className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-[#0a0e1c] font-display font-black text-lg py-4 rounded-xl flex items-center justify-center gap-2">
          {busy ? <><Loader2 className="animate-spin" size={18} /> Redirecting…</> : <>{status.connected ? "Re-" : ""}Connect to Xero <ExternalLink size={18} /></>}
        </button>
      )}
    </div>
  );
}
